import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Th, Td } from "@/pages/CustomersPage";
import SelectWithAdd from "@/components/SelectWithAdd";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Generic single-/multi-field master list page.
 * Now includes a Status column (Active/Inactive) and admin-only delete.
 */
export default function GenericMasterPage({
  resourcePath,
  testIdPrefix,
  overline = "Masters",
  title,
  description,
  fields,
  displayKeys,
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const empty = fields.reduce((acc, f) => ({ ...acc, [f.key]: "" }), { status: "Active" });

  const load = () => api.get(`/${resourcePath}`).then((r) => setItems(r.data));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ...fields.reduce((acc, f) => ({ ...acc, [f.key]: row[f.key] || "" }), {}),
      status: row.status || "Active",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && !form[f.key]) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/${resourcePath}/${editing.id}`, form);
      else await api.post(`/${resourcePath}`, form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/${resourcePath}/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-page`}>
      <PageHeader
        overline={overline}
        title={title}
        description={description}
        actions={
          <Button
            data-testid={`${testIdPrefix}-add-button`}
            onClick={openNew}
            className="rounded-none vsm-primary-bg hover:opacity-90"
          >
            <Plus className="w-4 h-4 mr-2" /> Add
          </Button>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-5 sm:py-8">
        <div className="border vsm-border vsm-surface overflow-x-auto vsm-table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b vsm-border text-left">
                {displayKeys.map((k) => (
                  <Th key={k}>{fields.find((f) => f.key === k)?.label || k}</Th>
                ))}
                <Th>Status</Th>
                <Th className="w-28 text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={displayKeys.length + 2} className="px-4 py-8 vsm-muted text-center">
                    No entries yet.
                  </td>
                </tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className="border-b vsm-border hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                  {displayKeys.map((k, i) => (
                    <Td key={k} className={i === 0 ? "font-semibold" : ""}>
                      {r[k] || "—"}
                    </Td>
                  ))}
                  <Td><StatusBadge status={r.status} /></Td>
                  <Td className="text-right">
                    <button
                      onClick={() => openEdit(r)}
                      data-testid={`${testIdPrefix}-edit-${r.id}`}
                      className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 mr-1"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => remove(r)}
                        data-testid={`${testIdPrefix}-delete-${r.id}`}
                        className="p-1.5 hover:bg-red-100 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border vsm-border max-w-md" data-testid={`${testIdPrefix}-dialog`}>
          <DialogHeader>
            <div className="vsm-overline">{editing ? "Edit" : "Create"}</div>
            <DialogTitle className="text-2xl font-bold tracking-tight">
              {editing ? `Edit ${title}` : `New ${title}`}
            </DialogTitle>
            <DialogDescription className="vsm-muted text-sm">{description}</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            {fields.map((f) => (
              <div key={f.key}>
                <Label className="vsm-overline">
                  {f.label} {f.required && <span className="text-red-600">*</span>}
                </Label>
                <Input
                  data-testid={`${testIdPrefix}-${f.key}`}
                  className="rounded-none mt-2"
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                />
              </div>
            ))}
            <div>
              <Label className="vsm-overline">Status</Label>
              <div className="mt-2">
                <SelectWithAdd
                  testId={`${testIdPrefix}-status`}
                  value={form.status || "Active"}
                  onChange={(v) => setForm({ ...form, status: v })}
                  options={[{ id: "Active", label: "Active" }, { id: "Inactive", label: "Inactive" }]}
                />
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setOpen(false)} data-testid={`${testIdPrefix}-cancel`}>Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-none vsm-primary-bg hover:opacity-90" data-testid={`${testIdPrefix}-save`}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
